/**
 * Drainage Data Service
 * Version: 1.0.0
 *
 * Service layer for drainage product data and AASHTO coefficients.
 * Fetches data from backend API instead of static import.
 */

import { logDebug, logWarn, logError } from '../utils/utils';

// Static fallback data for when API is unavailable
import STATIC_DRAINAGE_DATA from '../data/drainage-products.json';

// API Base URL - use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_BASEURL || 'http://localhost:3004';

/**
 * Interface for drainage product properties
 */
export interface IDrainageProductProperties {
    displayName: string;
    category: string;
    effectivePorosity: number;
    permeabilityCoefficient: number;
    transmissivity?: number;
    thickness?: number;
    rfcreep?: number;
    description?: string;
    databaseReference?: string;
    notes?: string;
    applicableLayersOnly?: string[];
}

/**
 * Interface for AASHTO coefficient table
 */
export interface IAashtoCoefficients {
    description: string;
    [quality: string]: string | Record<string, number>;
}

/**
 * Interface for the full drainage database structure
 */
export interface IDrainageDatabase {
    info: {
        version: string;
        description: string;
        lastUpdated: string;
        source: string;
        changes: string;
        units: Record<string, string>;
    };
    productCategories: Record<string, {
        description: string;
        hasDrainageCapability: boolean;
        applicableLayers: string[];
    }>;
    products: Record<string, IDrainageProductProperties>;
    drainageFormulas: {
        shortTerm: { description: string; formula: string; reference: string; units: string };
        longTerm: { description: string; formula: string; reference: string; units: string };
        timeConversions: Record<string, string>;
    };
    drainageQualityThresholds: Record<string, { maxHours?: number; description: string }>;
    aashtoCoefficients: {
        description: string;
        source: string;
        lastVerified: string;
        moistureExposureHeaders: string[];
        UNSTABILISED: IAashtoCoefficients;
        STABILISED: IAashtoCoefficients;
    };
    productIdentification: {
        geogridProducts: { description: string; patterns: string[]; characteristics: string };
        drainageProducts: { description: string; patterns: string[]; characteristics: string };
    };
    defaultProduct: string;
    notes: Record<string, string>;
}

/**
 * DrainageDataService - Singleton service for drainage data access
 *
 * IMPORTANT: Call DrainageDataService.ensureInitialized() at app startup before using other methods.
 * This fetches data from the backend API once and caches it for synchronous access.
 */
export class DrainageDataService {
    /** Cached drainage data */
    private static data: IDrainageDatabase | null = null;

    /** Initialization flag */
    private static initialized = false;

    /** Initialization promise for concurrent calls */
    private static initPromise: Promise<void> | null = null;

    /** Cache TTL in milliseconds (1 hour) */
    private static readonly CACHE_TTL_MS = 60 * 60 * 1000;

    /** Cache timestamp */
    private static cacheTimestamp = 0;

    /**
     * Ensure the service is initialized by fetching data from the API.
     * This should be called at app startup.
     * Safe to call multiple times - will only fetch once (or re-fetch if cache is stale).
     */
    public static async ensureInitialized(): Promise<void> {
        // If already initialized and cache is fresh, return immediately
        if (this.initialized && this.data && this.isCacheFresh()) {
            return;
        }

        // If initialization is in progress, wait for it
        if (this.initPromise) {
            return this.initPromise;
        }

        // Start initialization
        this.initPromise = this.fetchAndCacheData();

        try {
            await this.initPromise;
        } finally {
            this.initPromise = null;
        }
    }

    /**
     * Check if cache is still fresh
     */
    private static isCacheFresh(): boolean {
        return Date.now() - this.cacheTimestamp < this.CACHE_TTL_MS;
    }

    /**
     * Fetch data from API and cache it
     */
    private static async fetchAndCacheData(): Promise<void> {
        try {
            logDebug('DrainageDataService', `Fetching drainage products from API: ${API_BASE_URL}/api/data/products/drainage`);

            const response = await fetch(`${API_BASE_URL}/api/data/products/drainage`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                },
                cache: 'no-store',
            });

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }

            const data: IDrainageDatabase = await response.json();

            this.data = data;
            this.cacheTimestamp = Date.now();
            this.initialized = true;

            logDebug(
                'DrainageDataService',
                `Initialized with ${Object.keys(this.data.products).length} drainage products`,
                {
                    version: this.data.info.version,
                    lastUpdated: this.data.info.lastUpdated,
                }
            );
        } catch (error) {
            logError('DrainageDataService', 'Failed to fetch drainage data from API', error);

            // If we have stale cached data, keep using it
            if (this.data) {
                logWarn('DrainageDataService', 'Using stale cached data due to API fetch failure');
                return;
            }

            // Fallback to static data when API is unavailable and no cached data exists
            logWarn('DrainageDataService', 'Using static fallback data - API unavailable');
            this.data = STATIC_DRAINAGE_DATA as unknown as IDrainageDatabase;
            this.cacheTimestamp = Date.now();
            this.initialized = true;
        }
    }

    /**
     * Initialize the service synchronously (for backward compatibility)
     * Will throw if data hasn't been fetched yet. Call ensureInitialized() first.
     */
    private static init(): void {
        if (!this.initialized || !this.data) {
            if (this.data) {
                logWarn('DrainageDataService', 'Using cached data - call ensureInitialized() for fresh data');
                return;
            }

            throw new Error(
                'DrainageDataService not initialized. Call DrainageDataService.ensureInitialized() at app startup.'
            );
        }
    }

    /**
     * Get the full drainage database
     */
    public static getData(): IDrainageDatabase {
        this.init();
        if (!this.data) {
            throw new Error('Drainage data not loaded');
        }
        return this.data;
    }

    /**
     * Get all products
     */
    public static getProducts(): Record<string, IDrainageProductProperties> {
        return this.getData().products;
    }

    /**
     * Get AASHTO coefficients
     */
    public static getAashtoCoefficients() {
        return this.getData().aashtoCoefficients;
    }

    /**
     * Get a specific product by ID
     */
    public static getProduct(productId: string): IDrainageProductProperties | undefined {
        return this.getProducts()[productId];
    }

    /**
     * Get the default product
     */
    public static getDefaultProduct(): string {
        return this.getData().defaultProduct;
    }

    /**
     * Get default product properties
     */
    public static getDefaultProductProperties(): IDrainageProductProperties {
        const defaultId = this.getDefaultProduct();
        const product = this.getProduct(defaultId);
        if (!product) {
            throw new Error(`Default product '${defaultId}' not found`);
        }
        return product;
    }

    /**
     * Check if the service is initialized
     */
    public static isInitialized(): boolean {
        return this.initialized && this.data !== null;
    }

    /**
     * Reset service (for testing purposes)
     */
    public static reset(): void {
        this.data = null;
        this.initialized = false;
        this.initPromise = null;
        this.cacheTimestamp = 0;
    }

    /**
     * Set test data directly (for testing purposes only)
     * This allows tests to inject mock data without making API calls
     */
    public static setTestData(testData: IDrainageDatabase): void {
        this.data = testData;
        this.initialized = true;
        this.cacheTimestamp = Date.now();
    }

    /**
     * Force refresh data from API (bypasses cache)
     */
    public static async forceRefresh(): Promise<void> {
        this.cacheTimestamp = 0;
        await this.ensureInitialized();
    }
}
